import React, { useCallback, useRef } from 'react';
import axios, { CancelTokenSource } from 'axios';

export type AbortSignals = {
	abortSignal: AbortSignal;
	axiosCancelToken: CancelTokenSource;
	isAborted: () => boolean;
};

export const abortedRequestedMessage = 'Replaced by newer subsequent request';

export const useAbortSignals = () => {
	/**
	 * Because Axios uses a custom CancelToken but Apollo uses the
	 * AbortController API, both need to be tracked
	 */
	const activeAbortController = useRef<AbortController>();
	const activeCancellationToken = useRef<CancelTokenSource>();

	const createSignals = useCallback(
		() => {
			const abortController = new AbortController();
			activeAbortController.current = abortController;

			const { CancelToken } = axios;
			const cancelTokenSource = CancelToken.source();
			activeCancellationToken.current = cancelTokenSource;

			const abortSignals: AbortSignals = {
				abortSignal: abortController.signal,
				axiosCancelToken: cancelTokenSource,
				isAborted: () => activeAbortController.current!.signal.aborted,
			};

			return abortSignals;
		},
		[],
	);

	const clearSignals = useCallback(
		() => {
			activeAbortController.current = undefined;
			activeCancellationToken.current = undefined;
		},
		[],
	);

	// abort any prior active requests
	const sendCancelSignals = useCallback(
		() => {
			activeAbortController.current?.abort(abortedRequestedMessage);
			activeCancellationToken.current?.cancel(abortedRequestedMessage);
		},
		[activeAbortController, activeCancellationToken],
	);

	return [
		createSignals,
		clearSignals,
		sendCancelSignals,
	] as const;
};
